/**
 * React Query hooks — dropdown option sources shared across recruitment screens.
 *
 * - Job openings: for the intake + application filters (from the recruitment API).
 * - People (admins/users): for hiring-manager and recruiter dropdowns (reuses the
 *   dashboard's existing Users API — the recruitment contract stores these as FKs
 *   to `admins`, and Phase 1 exposes no dedicated people endpoint).
 */

import { useQuery } from '@tanstack/react-query';
import { RecruitmentApiService } from '../../../services/recruitmentApi';
import { UserApiService } from '../../../services/userApi';
import { HrmApiService } from '../../../services/hrmApi';
import type { DropdownOption } from '../../../components/ui/FloatingDropdown';

/** All job openings as {value,label} options (title + status hint). */
export const useJobOptions = () =>
	useQuery({
		queryKey: ['recruitment', 'jobs', 'options'],
		staleTime: 60 * 1000,
		queryFn: async (): Promise<DropdownOption[]> => {
			const resp = await RecruitmentApiService.listJobs({ page: 1, page_size: 200 });
			const rows = resp.data?.rows ?? [];
			return rows.map(j => ({ value: String(j.id), label: j.title }));
		},
	});

/** Admin/user accounts for hiring-manager / recruiter dropdowns. */
export const usePeopleOptions = () =>
	useQuery({
		queryKey: ['recruitment', 'people', 'options'],
		staleTime: 5 * 60 * 1000,
		queryFn: async (): Promise<DropdownOption[]> => {
			const resp = await UserApiService.getUsers({ limit: 500 });
			const users = Array.isArray(resp?.data) ? resp.data : [];
			return users.map(u => ({
				value: String(u.id),
				label: [u.firstName, u.lastName].filter(Boolean).join(' ').trim() || u.username || `User ${u.id}`,
			}));
		},
	});

/**
 * Departments for the job-opening form. `department_id` is an FK to
 * `hrm_departments` (shared org data), so we reuse the dashboard's HRM API.
 */
export const useDepartmentOptions = () =>
	useQuery({
		queryKey: ['recruitment', 'departments', 'options'],
		staleTime: 5 * 60 * 1000,
		queryFn: async (): Promise<DropdownOption[]> => {
			const resp = await HrmApiService.getDepartments({ is_active: true, limit: 200 });
			const body = resp as { data?: { data?: unknown[] } | unknown[] };
			const list = Array.isArray(body?.data)
				? (body.data as unknown[])
				: Array.isArray((body?.data as { data?: unknown[] })?.data)
					? ((body.data as { data?: unknown[] }).data as unknown[])
					: [];
			return (list as { id: number; name: string }[]).map(d => ({ value: String(d.id), label: d.name }));
		},
	});
